// React
import { useRef } from 'react';
// Apollo
import { useQuery } from '@apollo/client';
// Types
import { GetTopCreatorStores } from 'api/store/types/GetTopCreatorStores';
// Queries
import { GET_TOP_CREATOR_STORES } from 'api/store/queries';
// Constants
import { SEARCH } from 'constants/routes';
// UI Components
import Carousel from 'ui3/Carousel/Carousel';
// Components
import CreatorCard from './CreatorCard/CreatorCard';
import SectionHeader from 'components/common3/SectionHeader/SectionHeader';
import Slider, { Settings } from 'react-slick';
// Hooks
import { useCarouselArrows } from 'hooks';
// Styles
import styles from './TopCreatorsCarousel.module.scss';

const sliderSettings = {
  infinite: false,
  adaptiveHeight: false,
  speed: 500,
  swipeToSlide: true,
  variableWidth: true,
  slidesToScroll: 1,
  arrows: false,
  dots: false,
};

const TopCreatorsCarousel = () => {
  const sliderRef = useRef<Slider | null>(null);

  const { data, loading } = useQuery<GetTopCreatorStores>(
    GET_TOP_CREATOR_STORES,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  const stores = data?.stores?.entities || [];

  const { canScroll, atStart, atEnd, onBeforeChange } = useCarouselArrows(
    sliderRef,
    stores.length
  );

  const handlePrevClick = () => {
    sliderRef.current?.slickPrev();
  };

  const handleNextClick = () => {
    sliderRef.current?.slickNext();
  };

  if (loading || stores.length === 0) {
    return null;
  }

  const settings: Settings = {
    ...sliderSettings,
    beforeChange: onBeforeChange,
  };

  return (
    <section className={styles.section}>
      <SectionHeader
        title="Top Creators"
        onPrev={handlePrevClick}
        onNext={handleNextClick}
        seeAllLink={`${SEARCH}?showTop=true`}
        seeAllLinkTitle="See Creators"
        showArrows={canScroll}
        prevDisabled={atStart}
        nextDisabled={atEnd}
        className={styles.header}
      />
      <div className={styles.carouselWrapper}>
        <Carousel ref={sliderRef} {...settings} className={styles.carousel}>
          {stores.map((store) => (
            <CreatorCard key={store.id} store={store} />
          ))}
        </Carousel>
      </div>
    </section>
  );
};

export default TopCreatorsCarousel;
